import React, { useEffect, useState } from "react";
import axios from "axios";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import { ApiLink } from "../APILink/ApiLink";
import authHeader from "../Auth/authHeader";

/**
 * PaymentTypeSelect - A reusable select component for payment types
 * Props:
 *   value: selected value
 *   onChange: function (event) => void
 *   label: string (optional)
 *   required: boolean (optional)
 *   error: boolean (optional)
 *   helperText: string (optional)
 *   sx: style overrides (optional)
 */
export default function PaymentTypeSelect({
  value,
  onChange,
  label = "Mënyra e Pagesës",
  required = false,
  error = false,
  helperText = "",
  sx = {},
  ...props
}) {
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setFetchError("");
    axios
      .get(ApiLink.API_URL + "Accounts/paymenttype", { headers: authHeader() })
      .then((res) => {
        if (mounted) setPaymentTypes(res.data || []);
      })
      .catch(() => {
        if (mounted) setFetchError("Nuk u morën mënyrat e pagesës");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={onChange}
      variant="outlined"
      size="small"
      required={required}
      error={error || !!fetchError}
      helperText={fetchError || (error ? helperText : "")}
      InputLabelProps={{ shrink: true }}
      SelectProps={{
        displayEmpty: true,
        IconComponent: (props) => (
          <svg {...props} width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--cc-chart-bar-credit)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        ),
        MenuProps: {
          PaperProps: {
            className: "payment-type-menu-paper",
            sx: {
              maxHeight: 280,
              maxWidth: "min(460px, calc(100vw - 32px))",
              mt: 0.5,
              overflowY: "auto",
              borderRadius: "10px",
              background: "var(--app-surface)",
              color: "var(--app-text)",
              border: "1px solid var(--dg-border)",
              "& .MuiMenuItem-root": {
                fontSize: "13px",
                minHeight: 36,
                whiteSpace: "normal",
                wordBreak: "break-word",
                color: "var(--app-text)",
                "&:hover": { background: "var(--dg-row-hover)" },
              },
            },
          },
        },
      }}
      InputProps={{
        sx: {
          borderRadius: 8,
          height: 48,
          minHeight: 48,
          maxHeight: 48,
          background: 'var(--dg-input-bg)',
          color: 'var(--dg-input-text)',
          borderColor: 'var(--dg-input-border)',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--cc-chart-bar-credit)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--cc-chart-bar-credit)',
          },
          '& .MuiSelect-select, & .MuiInputBase-input': {
            height: 48,
            minHeight: 48,
            maxHeight: 48,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            padding: '0 14px',
          },
          ...sx,
        },
      }}
      FormControlProps={{
        sx: {
          borderRadius: 8,
          height: 48,
          minHeight: 48,
          maxHeight: 48,
        },
      }}
      sx={{
        "& .MuiInputLabel-root": { color: "var(--app-text-muted)" },
        "& .MuiInputLabel-root.Mui-focused": {
          color: "var(--cc-chart-bar-credit)",
        },
        "& .MuiFormHelperText-root": { color: "var(--app-text-muted)" },
      }}
      placeholder="Zgjidh mënyrën e pagesës"
      {...props}
    >
      <MenuItem value="" disabled sx={{ color: 'var(--app-text-muted)' }}>
        {loading ? <CircularProgress size={18} /> : "Zgjidh mënyrën e pagesës"}
      </MenuItem>
      <MenuItem key="0" value="0">
        Pagesa bëhet më vonë
      </MenuItem>
      {paymentTypes.map((option) => (
        <MenuItem key={option.AccountID} value={option.AccountID}>
          {option.AccountName}
        </MenuItem>
      ))}
    </TextField>
  );
}
